// Ambient style instruments (docs/AUDIO_DESIGN.md §12): a warm evolving pad (with an optional tonic drone and a
// cross-fed delay wash), a soft sub, a felt kit with a shaker and a glass bell lead. Every node goes in the voice registry:
// persistent parts tagged `${tag}:p`, pad notes `${tag}`, so a skip, a handoff and the soak free them.
import { mtof, hold, noise } from '../dsp.js';
import { gain, filter, osc, panner, persistent } from '../instruments.js';

// Instrument levels (tuning knobs for the mix balance, like instruments.js LEVELS).
export const AMB = { pad: 0.24, drone: 0.05, bass: 0.19, kit: 1.1, lead: 1.8 };

const mod12 = n => ((n % 12) + 12) % 12;
const stopAt = (s, t) => { try { s.stop(t); } catch { /* not started or already stopped */ } };

/* ---------------- pad: 2 detuned saws + a sine an octave up per note, bus LP with a slow LFO ---------------- */
/**
 * plan.ep: { lp, pad: { atk, rel, det, shim, lfo, depth, width, wash, drone } }. Per note: saws at ±det¢ (×0.6–1.4 at
 * random) + a sine at 2f (shim), amplitude 0.24·v^1.2 reached with τ atk/3, release τ rel (final chord: the engine's 0.9 s).
 * Notes < MIDI 57 sit in the centre, the others alternate ±width. One voice per pitch: a note whose pitch is still sounding
 * reuses that voice. Starting where it ends (within strum + 0.1 s) it ties: the release is cancelled and it carries on, so
 * a chord held over bars is one set of voices and common tones sustain across a chord change. Later, it swells back
 * (τ atk/3) from wherever its release got to: a soft re-articulation without phasing. Oscillators stop 5τ after the
 * note-off (−43 dB), release ×0.75 in lite (cap 10); stealing takes the voice released first, with a 0.3 s fade (τ 0.1 s).
 * Bus: 3 panners → LP (Q .6) at lp ± depth·lp (LFO at lfo Hz, +20 % swell over each fresh attack) → dry, reverb .45, and a
 * wash (LP 1.6k → delays of 3 and 5 steps cross-fed at .35, panned ∓.7). Drone: sine at the tonic (MIDI 48–59) + triangle
 * a 5th up (.3) → LP 700, ±30 % breathing at 0.07 Hz, following the form (creeps in over the intro, +2 dB in the break,
 * fades from the outro's final chord).
 */
export function createPad(ac, out, send, plan, { registry, tag, cap = 16, r, t0 = ac.currentTime, log = null, stepDur }) {
  const ep = plan.ep, P = ep.pad, nodes = [], src = [];
  const N = x => (nodes.push(x), x);
  const outG = N(gain(ac, 1)), sum = N(gain(ac, 1)), lp = N(filter(ac, 'lowpass', ep.lp, 0.6)), sendG = N(gain(ac, 0.45));
  const buses = [-P.width, 0, P.width].map(p => { const s = N(panner(ac, p)); s.connect(sum); return s; });
  sum.connect(lp); lp.connect(outG); lp.connect(sendG); outG.connect(out); sendG.connect(send);
  const lfo = N(osc(ac, 'sine', P.lfo)), lfoG = N(gain(ac, ep.lp * P.depth));
  lfo.connect(lfoG); lfoG.connect(lp.frequency); src.push(lfo);
  const wIn = N(gain(ac, P.wash)), wLP = N(filter(ac, 'lowpass', 1600, 0.5)), dL = N(ac.createDelay(2)), dR = N(ac.createDelay(2));
  const fL = N(gain(ac, 0.35)), fR = N(gain(ac, 0.35)), pL = N(panner(ac, -0.7)), pR = N(panner(ac, 0.7));
  dL.delayTime.value = Math.min(1.9, 3 * stepDur); dR.delayTime.value = Math.min(1.9, 5 * stepDur);
  lp.connect(wIn); wIn.connect(wLP); wLP.connect(dL); wLP.connect(dR);
  dL.connect(fL); fL.connect(dR); dR.connect(fR); fR.connect(dL); dL.connect(pL); dR.connect(pR); pL.connect(outG); pR.connect(outG);
  if (P.drone) {
    const m = 48 + mod12(plan.key.tonic - 48);
    const d1 = N(osc(ac, 'sine', mtof(m))), d2 = N(osc(ac, 'triangle', mtof(m + 7))), d2g = N(gain(ac, 0.3)), dLP = N(filter(ac, 'lowpass', 700, 0.5));
    const dMix = N(gain(ac, 1)), dAmp = N(gain(ac, 0)), dLfo = N(osc(ac, 'sine', 0.07)), dLfoG = N(gain(ac, 0.3));
    d1.connect(dLP); d2.connect(d2g); d2g.connect(dLP); dLP.connect(dMix); dMix.connect(dAmp); dAmp.connect(buses[1]);
    dLfo.connect(dLfoG); dLfoG.connect(dMix.gain); src.push(d1, d2, dLfo);
    const D = AMB.drone, bar = 16 * stepDur;
    let ts = t0;
    for (const s of plan.sections) {
      if (s.name === 'intro') dAmp.gain.setTargetAtTime(0.7 * D, ts + bar, (s.bars * bar) / 3);
      else if (s.name === 'outro') dAmp.gain.setTargetAtTime(0, ts + 2 * bar, 0.6 * bar);
      else dAmp.gain.setTargetAtTime(s.name === 'break' ? 1.25 * D : D, ts, bar / 2);
      ts += s.bars * bar;
    }
  }
  const bus = persistent(registry, tag, src, nodes, t0);
  let voices = [], maxActive = 0, steals = 0, notes = 0;               // { v, midi, amp, off, end: sounding end (−35 dB) }

  const steal = t => {
    let k = 0;
    for (let i = 1; i < voices.length; i++) if (voices[i].off < voices[k].off) k = i;
    const old = voices.splice(k, 1)[0];
    registry.stop(old.v, t, 0.1);                   // a 0.3 s fade, not a cut
    old.end = t; if (old.rec) old.rec[1] = t;
    steals++;
  };
  const note = (t, midi, vel, dur, win, relTau) => {
    const off = t + Math.max(0.1, dur), A = AMB.pad * Math.pow(vel, 1.2);
    voices = voices.filter(x => x.end > t);
    const y = voices.find(z => z.midi === midi);
    if (y) {
      const g = y.amp.gain, tie = t < y.off + win && y.off > ac.currentTime + 0.02, o2 = tie ? Math.max(off, y.off) : off, end = o2 + 5 * relTau;
      if (tie) { g.cancelScheduledValues(y.off); g.setTargetAtTime(A, y.off, 1.2); }   // drop the release, drift to the new level
      else g.setTargetAtTime(A, t, P.atk / 3);                                           // swell back from the release
      g.setTargetAtTime(0, o2, relTau);
      for (const o of y.v.src) stopAt(o, end);
      y.v.end = end; y.off = o2; y.end = o2 + 4 * relTau;
      if (y.rec) y.rec[1] = y.end;
      return !tie;
    }
    if (voices.length >= cap) steal(t);
    const f = mtof(midi), d = P.det * (0.6 + 0.8 * r()), end = off + 5 * relTau;
    const o1 = osc(ac, 'sawtooth', f), o2 = osc(ac, 'sawtooth', f), o3 = osc(ac, 'sine', 2 * f), sg = gain(ac, P.shim), amp = gain(ac, 0);
    o1.detune.value = d; o2.detune.value = -d;
    o1.connect(amp); o2.connect(amp); o3.connect(sg); sg.connect(amp); amp.connect(buses[midi < 57 ? 1 : midi % 2 ? 0 : 2]);
    amp.gain.setValueAtTime(0, t);
    amp.gain.setTargetAtTime(A, t, P.atk / 3);
    amp.gain.setTargetAtTime(0, off, relTau);
    for (const o of [o1, o2, o3]) { o.start(t); o.stop(end); }
    const v = registry.add({ src: [o1, o2, o3], all: [o1, o2, o3, sg, amp], start: t, end, tag, out: amp });
    const x = { v, midi, amp, off, end: off + 4 * relTau, rec: log ? [t, off + 4 * relTau] : null };
    if (x.rec) log.push(x.rec);
    voices.push(x);
    notes++;
    maxActive = Math.max(maxActive, voices.length);
    return true;
  };
  return {
    /** spread bottom to top over `strum` s; the top voice gets +10 % velocity; rel ≥ 0.5 (the final chord) sets the release */
    chord(t, midis, vel, dur, strum = 0.04, rel = 0.1) {
      const n = midis.length, relTau = rel >= 0.5 ? rel : P.rel * (cap <= 10 ? 0.75 : 1), win = strum + 0.1;
      let fresh = false;
      midis.forEach((m, k) => {
        const dt = n > 1 ? (k * strum) / (n - 1) : 0;
        if (note(t + dt, m, k === n - 1 ? Math.min(1, vel * 1.1) : vel, dur - dt, win, relTau)) fresh = true;
      });
      if (fresh) { lp.frequency.setTargetAtTime(ep.lp * 1.2, t, P.atk / 2); lp.frequency.setTargetAtTime(ep.lp, t + P.atk, 2.5); }
    },
    stats: () => ({ maxActive, steals, notes, active: voices.length }),
    dispose(t) {
      const a = Math.max(ac.currentTime, t - 1.5);  // tails and the wash fade before the bus is freed
      hold(outG.gain, a); outG.gain.setTargetAtTime(0, a, 0.25);
      hold(sendG.gain, a); sendG.gain.setTargetAtTime(0, a, 0.25);
      bus.dispose(t);
    },
  };
}

/* ---------------- sub: sine + triangle (.22) → LP 420 Hz, slow gate ---------------- */
/** Amplitude AMB.bass·v with τ 0.12 s (a ~0.35 s swell, no thump), release τ 0.45 s; a note following a sounding one
 *  glides to its pitch (τ 30 ms), so chord changes are legato; after a silence it starts on pitch. */
export function createSub(ac, out, { registry, tag, t0 = ac.currentTime }) {
  const sine = osc(ac, 'sine', 55), tri = osc(ac, 'triangle', 55), triG = gain(ac, 0.22);
  const lp = filter(ac, 'lowpass', 420, 0.5), amp = gain(ac, 0);
  sine.connect(lp); tri.connect(triG); triG.connect(lp); lp.connect(amp); amp.connect(out);
  const p = persistent(registry, tag, [sine, tri], [sine, tri, triG, lp, amp], t0);
  let lastOff = -Infinity;
  return {
    note(t, midi, vel, dur) {
      const f = mtof(midi), off = t + Math.max(0.3, dur);
      for (const o of [sine, tri]) { if (t > lastOff + 1.5) o.frequency.setValueAtTime(f, t); else o.frequency.setTargetAtTime(f, t, 0.03); }
      amp.gain.setTargetAtTime(AMB.bass * vel, t, 0.12);
      amp.gain.setTargetAtTime(0, off, 0.45);
      lastOff = off;
    },
    dispose(t) { p.dispose(t); },
  };
}

/* ---------------- felt kit: soft kick without a click, shaker, woody tock, brush, hat; one noise source ---------------- */
/**
 * kit.lp: the bus LP (kick, tock, brush). Kick: sine 95 → 48 Hz (τ 45 ms), 0.38v, attack τ 4 ms, decay τ 180 ms.
 * Shaker: noise → BP 4.2k (Q .9), 0.55v, attack τ 6 ms, decay τ 60 ms, pan +.25. Tock: sines 820 + 1720 Hz (.25) → 0.22v,
 * τ 30 ms, pan −.2. Brush: noise → BP 1.8k (Q .7), 0.3v, τ 110 ms. Hat: noise → HP 7k, 0.08v, τ 25 ms (open 140 ms).
 * Reverb send .25 (shaker, tock, brush).
 */
export function createFeltKit(ac, out, send, kit, { registry, tag, t0 = ac.currentTime }) {
  const L = AMB.kit, nodes = [];
  const N = x => (nodes.push(x), x);
  const src = N(ac.createBufferSource());
  src.buffer = noise(ac, 'white'); src.loop = true;
  const bus = N(filter(ac, 'lowpass', kit.lp, 0.5)), sendG = N(gain(ac, 0.25));
  bus.connect(out); sendG.connect(send);
  const kOsc = N(osc(ac, 'sine', 48)), kAmp = N(gain(ac, 0));
  kOsc.connect(kAmp); kAmp.connect(bus);
  const xBP = N(filter(ac, 'bandpass', 4200, 0.9)), xAmp = N(gain(ac, 0)), xPan = N(panner(ac, 0.25));
  src.connect(xBP); xBP.connect(xAmp); xAmp.connect(xPan); xPan.connect(out); xPan.connect(sendG);
  const r1 = N(osc(ac, 'sine', 820)), r2 = N(osc(ac, 'sine', 1720)), r2g = N(gain(ac, 0.25)), rAmp = N(gain(ac, 0)), rPan = N(panner(ac, -0.2));
  r1.connect(rAmp); r2.connect(r2g); r2g.connect(rAmp); rAmp.connect(rPan); rPan.connect(bus); rPan.connect(sendG);
  const sBP = N(filter(ac, 'bandpass', 1800, 0.7)), sAmp = N(gain(ac, 0));
  src.connect(sBP); sBP.connect(sAmp); sAmp.connect(bus); sAmp.connect(sendG);
  const hHP = N(filter(ac, 'highpass', 7000, 0.7)), hAmp = N(gain(ac, 0)), hPan = N(panner(ac, 0.2));
  src.connect(hHP); hHP.connect(hAmp); hAmp.connect(hPan); hPan.connect(out);
  const p = persistent(registry, tag, [src, kOsc, r1, r2], nodes, t0);
  const hit = (param, peak, t, atk, rel, tau) => { param.setTargetAtTime(peak, t, atk); param.setTargetAtTime(0, t + rel, tau); };
  return {
    kick(t, v) {
      kOsc.frequency.setValueAtTime(95, t); kOsc.frequency.setTargetAtTime(48, t, 0.045);
      hit(kAmp.gain, 0.38 * v * L, t, 0.004, 0.012, 0.18);
    },
    snare(t, v) { hit(sAmp.gain, 0.3 * v * L, t, 0.005, 0.01, 0.11); },
    hat(t, v, open = false) { hit(hAmp.gain, 0.08 * v * L, t, 0.002, 0.004, open ? 0.14 : 0.025); },
    rim(t, v) { hit(rAmp.gain, 0.22 * v * L, t, 0.0015, 0.003, 0.03); },
    shaker(t, v) { hit(xAmp.gain, 0.55 * v * L, t, 0.006, 0.012, 0.06); },
    dispose(t) { p.dispose(t); },
  };
}

/* ---------------- lead: glass bell, 2 FM voices round robin, dotted-quarter delay ---------------- */
/**
 * lead: { timbre, pan }. 'soft' FM 1:2 (index 0.7), 'vibes' 1:3.5 (index 1.1, glassier); index → 0.05 (τ 0.25 s);
 * amplitude 0.16·v·AMB.lead with τ 8 ms, then decays τ 1.3 s (a bell: no note-off). A voice is damped (τ 8 ms) 30 ms
 * before it takes a new pitch. Sparse by construction: a note < max(1.2 s, 5.5 steps) after the last one is skipped.
 * LP 2.8k → pan; delay 6 steps (fb .42 through LP 1.8k, wet .45, panned to the other side); reverb .5.
 */
export function createBell(ac, out, send, lead, { registry, tag, stepDur, t0 = ac.currentTime }) {
  const nodes = [], src = [];
  const N = x => (nodes.push(x), x);
  const glass = lead.timbre === 'vibes', ratio = glass ? 3.5 : 2, index = glass ? 1.1 : 0.7, side = (lead.pan || 0) > 0 ? -0.4 : 0.4;
  const sum = N(gain(ac, 1)), lp = N(filter(ac, 'lowpass', 2800, 0.5)), pan = N(panner(ac, lead.pan || 0)), sendG = N(gain(ac, 0.5));
  const dly = N(ac.createDelay(2.5)), dLP = N(filter(ac, 'lowpass', 1800, 0.5)), fb = N(gain(ac, 0.42)), wet = N(gain(ac, 0.45)), wPan = N(panner(ac, side));
  dly.delayTime.value = Math.min(2.4, 6 * stepDur);
  sum.connect(lp); lp.connect(pan); lp.connect(dly); dly.connect(dLP); dLP.connect(fb); fb.connect(dly); dLP.connect(wet); wet.connect(wPan);
  pan.connect(out); pan.connect(sendG); wPan.connect(out); wPan.connect(sendG); sendG.connect(send);
  const vs = [0, 1].map(() => {
    const car = N(osc(ac, 'sine', 440)), mod = N(osc(ac, 'sine', 440 * ratio)), modG = N(gain(ac, 0)), amp = N(gain(ac, 0));
    mod.connect(modG); modG.connect(car.frequency); car.connect(amp); amp.connect(sum);
    src.push(car, mod);
    return { car, mod, modG, amp };
  });
  const p = persistent(registry, tag, src, nodes, t0);
  const gap = Math.max(1.2, 5.5 * stepDur);
  let k = 0, last = -Infinity;
  return {
    note(t, midi, vel) {
      if (t - last < gap) return;
      last = t; k = 1 - k;
      const v = vs[k], f = mtof(midi), a = Math.max(ac.currentTime, t - 0.03);
      hold(v.amp.gain, a); v.amp.gain.setTargetAtTime(0, a, 0.008);
      hold(v.modG.gain, a);
      v.car.frequency.setValueAtTime(f, t); v.mod.frequency.setValueAtTime(ratio * f, t);
      v.modG.gain.setValueAtTime(index * ratio * f, t); v.modG.gain.setTargetAtTime(0.05 * ratio * f, t, 0.25);
      v.amp.gain.setTargetAtTime(0.16 * vel * AMB.lead, t, 0.008); v.amp.gain.setTargetAtTime(0, t + 0.025, 1.3);
    },
    dispose(t) { p.dispose(t); },
  };
}
